/**
 * La maquetación en el Studio (25-sep-2026: «si los plugins maquetan, este también»). Un botón, sin
 * IA y sin coste: el motor hace el PDF de imprenta para KDP y el EPUB desde tu manuscrito, con el
 * navegador que ya tienes (Chrome o Edge), y los deja en la carpeta Publicacion de tu libro.
 */
import { useState } from "react";
import { maquetar, mostrarEnCarpeta } from "../api";
import { nombreDeRuta } from "../logica";
import type { Maquetacion, Trabajo } from "../api";
import { ListaDePasos } from "./ListaDePasos";

const TAMANOS: [string, string][] = [["6x9", "6 × 9 pulgadas (la más común en KDP)"], ["5.5x8.5", "5,5 × 8,5 pulgadas"], ["5x8", "5 × 8 pulgadas (bolsillo)"]];

export default function Maquetar({ carpeta, doc, trabajo, alLanzar }: {
  carpeta: string; doc: string | null; trabajo: Trabajo | null; alLanzar: () => void;
}) {
  const [tamano, setTamano] = useState("6x9");
  const [papel, setPapel] = useState("crema");
  const [fallo, setFallo] = useState<string | null>(null);
  const [viejo, setViejo] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const enCurso = trabajo?.estado === "en_curso";
  const hecho = trabajo?.estado === "hecho" ? (trabajo.resultado as unknown as Maquetacion) : null;
  // La portadilla: lo que el autor escriba manda; si no escribe nada, el motor la saca de la primera
  // página y lo dice. Se rellena con lo que salió la última vez para poder corregirlo.
  const [titulo, setTitulo] = useState(hecho?.titulo ?? "");
  const [autor, setAutor] = useState(hecho?.autor ?? "");

  async function lanzar() {
    if (!doc) return;
    setOcupado(true); setFallo(null);
    try {
      const r = await maquetar(carpeta, doc, { tamano, papel, titulo: titulo.trim(), autor: autor.trim() });
      if (r === null) setViejo(true); else alLanzar();
    } catch (e) { setFallo((e as Error).message); }
    finally { setOcupado(false); }
  }

  return (
    <section className="carta">
      <h2>Maquetar tu libro<span className="sub">{doc ? nombreDeRuta(doc) : "sin manuscrito"}</span></h2>
      <p className="vacio">
        El PDF de imprenta para KDP (capítulos que empiezan en página impar, cabeceras, números de página,
        márgenes de lomo y notas al final de cada capítulo) y el EPUB para Kindle, desde tu manuscrito. No usa IA ni cuesta nada. Se guardan en la
        carpeta <b>Publicacion</b> de tu libro.
      </p>
      {viejo && <div className="aviso">Tu motor aún no sabe maquetar: instala la última versión del Studio (en Ajustes → Descargas).</div>}
      {!enCurso && (
        <div className="formulario">
          <label className="campo"><span>Título del libro</span>
            <input className="escribir" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Si lo dejas vacío, lo saco de la primera página" />
          </label>
          <label className="campo"><span>Autor</span>
            <input className="escribir" value={autor} onChange={(e) => setAutor(e.target.value)} placeholder="Tu nombre o tu seudónimo" />
          </label>
          <label className="campo"><span>Tamaño</span>
            <select className="escribir" value={tamano} onChange={(e) => setTamano(e.target.value)}>
              {TAMANOS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </label>
          <label className="campo"><span>Papel (para el lomo)</span>
            <select className="escribir" value={papel} onChange={(e) => setPapel(e.target.value)}>
              <option value="crema">Crema</option><option value="blanco">Blanco</option>
            </select>
          </label>
          <div className="pie-form">
            <p className="vacio">Tarda unos segundos · sin coste</p>
            <button className="btn" disabled={!doc || ocupado} onClick={lanzar}>{hecho ? "Maquetar otra vez" : "Maquetar mi libro"}</button>
          </div>
        </div>
      )}
      {trabajo && trabajo.estado !== "hecho" && <ListaDePasos trabajo={trabajo} />}
      {trabajo?.estado === "fallido" && <p className="vacio" style={{ color: "var(--acc)" }}>No se ha podido maquetar: {trabajo.error}</p>}
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
      {hecho && (
        <div className="maqueta-hecha">
          <div className="rejilla">
            <div className="cifra"><b>{hecho.paginas ?? "—"}</b><span>páginas</span></div>
            <div className="cifra"><b>{hecho.lomo ? `${String(hecho.lomo.mm).replace(".", ",")} mm` : "—"}</b><span>lomo ({hecho.papel})</span></div>
            <div className="cifra"><b>{hecho.capitulos}</b><span>capítulos{hecho.partes ? ` · ${hecho.partes} partes` : ""}</span></div>
          </div>
          <ul className="maqueta-ficheros">
            {hecho.pdf && <li><b>PDF de imprenta</b><span className="mono">{nombreDeRuta(hecho.pdf)}</span> <button className="btn texto" onClick={() => mostrarEnCarpeta(hecho.pdf!)}>Enseñar en la carpeta</button></li>}
            <li><b>EPUB para Kindle</b><span className="mono">{nombreDeRuta(hecho.epub)}</span> <button className="btn texto" onClick={() => mostrarEnCarpeta(hecho.epub)}>Enseñar en la carpeta</button></li>
          </ul>
          {hecho.avisos.map((a, i) => <div className="aviso" key={i}>{a}</div>)}
          <p className="vacio">
            El lomo sale del número de páginas del PDF ya maquetado: úsalo para la cubierta. Si cambias el manuscrito,
            vuelve a maquetar y el lomo se recalcula.
          </p>
        </div>
      )}
    </section>
  );
}
